import { getOfferTypeClassificationType } from "@/app/utils/helpers/backend/offers/get-offer-type-classification";
import type {
  OfferFormDataT,
  OfferVehicleConditionT,
} from "@/app/types/offers";

type OfferListPriceSavingsInputT = {
  pricing?: OfferFormDataT["pricing"] | null;
  offer_types?: OfferFormDataT["offer_types"];
  vehicle_condition?: OfferVehicleConditionT;
};

export type OfferListPriceSavingsT = {
  uvpListenpreis: number;
  vergleichspreis: number;
  ersparnisBetrag: number;
  ersparnisProzent: number;
};

const formatPriceAmount = (value: number): string =>
  new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

const getComparablePurchasePrice = (
  offer: OfferListPriceSavingsInputT
): number | null => {
  const pricing = offer.pricing;
  if (!pricing?.type) {
    return null;
  }

  if (pricing.type === "leasing") {
    return pricing.leasing?.anschaffungspreis ?? null;
  }

  const isBusiness =
    getOfferTypeClassificationType(offer.offer_types) === "business";
  const isNew = offer.vehicle_condition === "new";

  if (pricing.type === "barkauf") {
    if (isBusiness && isNew && pricing.barkauf?.anschaffungspreisNetto) {
      return pricing.barkauf.anschaffungspreisNetto;
    }

    return pricing.barkauf?.anschaffungspreisBrutto ?? null;
  }

  if (pricing.type === "finanzierung") {
    if (isBusiness && isNew && pricing.finanzierung?.anschaffungspreisNetto) {
      return pricing.finanzierung.anschaffungspreisNetto;
    }

    return pricing.finanzierung?.anschaffungspreisBrutto ?? null;
  }

  return null;
};

const getUvpListenpreis = (
  offer: OfferListPriceSavingsInputT
): number | null => {
  const pricing = offer.pricing;
  if (!pricing?.type) {
    return null;
  }

  if (pricing.type === "leasing") {
    return pricing.leasing?.uvpListenpreis ?? null;
  }

  if (pricing.type === "barkauf") {
    return pricing.barkauf?.uvpListenpreis ?? null;
  }

  if (pricing.type === "finanzierung") {
    return pricing.finanzierung?.uvpListenpreis ?? null;
  }

  return null;
};

export const getOfferListPriceSavings = (
  offer: OfferListPriceSavingsInputT
): OfferListPriceSavingsT | null => {
  const uvpListenpreis = getUvpListenpreis(offer);
  const vergleichspreis = getComparablePurchasePrice(offer);

  if (
    uvpListenpreis == null ||
    vergleichspreis == null ||
    uvpListenpreis <= 0 ||
    vergleichspreis <= 0 ||
    uvpListenpreis <= vergleichspreis
  ) {
    return null;
  }

  const ersparnisBetrag = uvpListenpreis - vergleichspreis;
  const ersparnisProzent = Math.round((ersparnisBetrag / uvpListenpreis) * 100);

  if (ersparnisProzent <= 0) {
    return null;
  }

  return {
    uvpListenpreis,
    vergleichspreis,
    ersparnisBetrag,
    ersparnisProzent,
  };
};

export const getOfferListPriceSavingsDisclaimer = (
  offer: OfferListPriceSavingsInputT
): string | null => {
  const savings = getOfferListPriceSavings(offer);

  if (!savings) {
    return null;
  }

  return `Listenpreis des Herstellers: ${formatPriceAmount(savings.uvpListenpreis)} € - mit diesem Angebot sparen Sie ${savings.ersparnisProzent} Prozent.`;
};
